import React, { useState, Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import {
  IdentificationIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { ArrowsRightLeftIcon } from '@heroicons/react/20/solid';

export default function ProfileMembershipSwitcher({
  memberships,
  currentMembershipId,
  switchUrl,
  currency = 'USD'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitch = (membershipId) => {
    if (membershipId === currentMembershipId) {
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    window.location.href = `${switchUrl}?fund_user_id=${membershipId}`;
  };

  const formatCurrency = (cents) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const currentMembership = memberships.find(m => m.id === currentMembershipId);
  const otherMemberships = memberships.filter(m => m.id !== currentMembershipId);

  return (
    <>
      {/* Switch Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex flex-col items-center gap-1 px-3 py-2 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
      >
        <ArrowsRightLeftIcon className="w-5 h-5" />
        <span>Switch</span>
      </button>

      {/* Modal */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="relative w-full max-w-lg transform rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <IdentificationIcon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <DialogTitle className="text-base font-semibold text-gray-900">
                            Switch Membership
                          </DialogTitle>
                          <p className="text-sm text-gray-500">
                            Select which membership to manage
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Membership List */}
                  <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                    {/* Current Membership */}
                    {currentMembership && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">
                          Currently Viewing
                        </p>
                        <MembershipCard
                          membership={currentMembership}
                          isCurrent={true}
                          formatCurrency={formatCurrency}
                          onClick={() => {}}
                        />
                      </div>
                    )}

                    {/* Other Memberships */}
                    {otherMemberships.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">
                          Other Memberships
                        </p>
                        <div className="space-y-2">
                          {otherMemberships.map((membership) => (
                            <MembershipCard
                              key={membership.id}
                              membership={membership}
                              isCurrent={false}
                              formatCurrency={formatCurrency}
                              onClick={() => handleSwitch(membership.id)}
                              disabled={isLoading}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                    <p className="text-xs text-gray-500 text-center">
                      You have {memberships.length} membership{memberships.length !== 1 ? 's' : ''} with this organization
                    </p>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function MembershipCard({ membership, isCurrent, formatCurrency, onClick, disabled }) {
  const { fundName, membershipId, balance, isOverdue, isAutopay } = membership;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isCurrent || disabled}
      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
        isCurrent
          ? 'border-indigo-300 bg-indigo-50 cursor-default'
          : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left Side */}
        <div className="flex items-start gap-3 min-w-0">
          {/* Icon */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
            isCurrent
              ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
              : 'bg-gray-200 group-hover:bg-gradient-to-br group-hover:from-indigo-500 group-hover:to-purple-600'
          }`}>
            <IdentificationIcon className="w-6 h-6 text-white" />
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{fundName}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Member ID: <span className="font-medium text-gray-700">#{membershipId}</span>
            </p>

            {/* Status Badges */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {isOverdue && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <ExclamationCircleIcon className="w-3 h-3" />
                  Overdue
                </span>
              )}
              {isAutopay && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <ArrowPathIcon className="w-3 h-3" />
                  AutoPay
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Balance & Action */}
        <div className="flex-shrink-0 text-right">
          <p className="text-xs text-gray-500">Balance</p>
          <p className={`text-lg font-bold ${
            balance > 0
              ? (isOverdue ? 'text-red-600' : 'text-amber-600')
              : 'text-green-600'
          }`}>
            {formatCurrency(balance)}
          </p>

          {isCurrent ? (
            <div className="mt-2 flex items-center justify-end gap-1 text-indigo-600">
              <CheckCircleIcon className="w-4 h-4" />
              <span className="text-xs font-medium">Current</span>
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-400">Click to switch</p>
          )}
        </div>
      </div>
    </button>
  );
}
